/**
 * Saisie du code de l'artifact (§3.2).
 *
 * Composant réutilisé par le formulaire de déploiement initial et par le
 * redéploiement d'une application existante (§3.4).
 *
 * ## La coloration d'une zone de saisie
 *
 * Un `<textarea>` ne sait pas afficher de couleurs : son contenu est du texte
 * brut. La technique employée ici est celle des éditeurs légers — un `<pre>`
 * colorisé posé **exactement** sous une zone de saisie au texte transparent, dont
 * on ne garde que le curseur et la sélection.
 *
 * Tout repose alors sur un alignement au caractère près, et rien ne le garantit
 * automatiquement. Les deux couches partagent donc police, corps, interligne et
 * marges intérieures. `wrap="off"` supprime le retour à la ligne automatique :
 * c'est ce que fait un éditeur de code, cela rend la numérotation exacte — elle
 * comptait les lignes logiques quand la zone en affichait davantage — et cela rend
 * la hauteur calculable, puisqu'une ligne de code occupe exactement une ligne.
 *
 * La hauteur suit donc le contenu, et le champ ne défile plus verticalement : il
 * n'y a ni barre à droite du code ni poignée de redimensionnement. Seul le
 * défilement horizontal subsiste, et la doublure le suit.
 *
 * Le balisage est injecté tel quel, ce qui n'est acceptable que parce que le
 * moteur échappe son entrée : voir `lib/highlight.ts`.
 */

import { ACCEPTED_ARTIFACT_EXTENSIONS, MAX_ARTIFACT_CODE_BYTES } from '@portail/shared';
import clsx from 'clsx';
import { Upload } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type UIEvent,
} from 'react';

import { useHighlightedCode } from '../hooks/useHighlightedCode';
import { formatBytes, formatNumber } from '../lib/format';
import { MAX_HIGHLIGHT_CHARACTERS } from '../lib/highlight';
import { FOCUS_RING, Label } from './ui';

/**
 * Réglages typographiques communs aux deux couches.
 *
 * Regroupés dans une constante unique : les répartir entre la zone de saisie et
 * sa doublure colorisée reviendrait à parier qu'un futur ajustement pense à
 * modifier les deux, et un corps de texte différent de quelques dixièmes suffit à
 * décaler le curseur en fin de ligne.
 */
const CODE_TYPOGRAPHY = 'px-3 py-3 font-mono text-xs leading-5';

/** Largeur de la colonne de numérotation. */
const GUTTER_WIDTH = '3.25rem';

/**
 * Retrait à gauche du code, numérotation comprise.
 *
 * La numérotation étant superposée et non côte à côte, c'est ce retrait qui
 * dégage sa place. Les deux couches le partagent, comme le reste de leur
 * typographie : un écart d'un pixel décalerait le curseur du texte coloré.
 */
const CODE_LEFT_PADDING = `calc(${GUTTER_WIDTH} + 0.75rem)`;

/** Attribut `accept` du sélecteur de fichier, dérivé de la liste partagée. */
const ACCEPT = ACCEPTED_ARTIFACT_EXTENSIONS.join(',');

/** Taille en octets d'une chaîne, mesurée comme le fait le serveur. */
export function byteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

/** Hauteur d'une ligne de code, en accord avec `leading-5` de la typographie. */
const LINE_HEIGHT_REM = 1.25;

/** Marges intérieures verticales cumulées, en accord avec `py-3`. */
const VERTICAL_PADDING_REM = 1.5;

/** Propriétés du champ de code. */
export interface CodeInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Hauteur **minimale**, en lignes. Au-delà, la zone s'ajuste au contenu. */
  rows?: number;
  label?: string;
  /**
   * Fait défiler la page jusqu'à la suite du formulaire après un collage.
   *
   * Utile là où les champs suivent la zone de code : un artifact de cinq cents
   * lignes repousse alors le bouton de mise à jour à des milliers de pixels, et
   * l'utilisateur doit dérouler à l'aveugle pour le retrouver. Inutile — et
   * nuisible — sur un formulaire en deux colonnes, où descendre éloignerait des
   * champs restés en haut à droite.
   */
  revealFieldsOnPaste?: boolean;
}

/** Zone de saisie du code, avec dépôt de fichier et numérotation de lignes. */
export function CodeInput({
  value,
  onChange,
  rows = 18,
  label = "Code de l'artifact",
  revealFieldsOnPaste = false,
}: CodeInputProps) {
  const textareaId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLPreElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const lines = useMemo(() => value.split('\n'), [value]);
  const size = useMemo(() => byteLength(value), [value]);
  const tooLarge = size > MAX_ARTIFACT_CODE_BYTES;

  const { result: highlighted, unavailable } = useHighlightedCode(value);

  /**
   * Hauteur ajustée au contenu, `rows` faisant office de plancher.
   *
   * La zone de saisie n'a donc **aucun défilement vertical** : c'est la page qui
   * défile. Une barre à droite du code, doublée de la poignée de
   * redimensionnement, encombrait un champ dont on veut surtout qu'il se lise.
   *
   * Le calcul est exact parce que le retour à la ligne automatique est désactivé :
   * une ligne de code occupe une ligne à l'écran, sans exception. C'est aussi ce
   * qui rend la numérotation juste.
   */
  const heightRem = Math.max(rows, lines.length) * LINE_HEIGHT_REM + VERTICAL_PADDING_REM;

  /**
   * Après un collage, amène la suite du formulaire à l'écran.
   *
   * Déclenché sur la transition « vide → rempli », et non à chaque frappe : c'est
   * le collage qui fait basculer la page de quelques centaines à plusieurs milliers
   * de pixels, et laisse l'utilisateur chercher le bouton d'envoi.
   */
  const wasEmpty = useRef(value.trim().length === 0);
  useEffect(() => {
    const empty = value.trim().length === 0;
    const justFilled = wasEmpty.current && !empty;
    wasEmpty.current = empty;

    if (!justFilled || !revealFieldsOnPaste) return;
    footerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [value, revealFieldsOnPaste]);

  /** Charge un fichier déposé ou sélectionné, après contrôle de taille. */
  const loadFile = useCallback(
    (file: File) => {
      setFileError(null);

      const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
      if (!(ACCEPTED_ARTIFACT_EXTENSIONS as readonly string[]).includes(extension)) {
        setFileError(
          `Le fichier « ${file.name} » n'est pas d'un type accepté. ` +
            `Extensions autorisées : ${ACCEPTED_ARTIFACT_EXTENSIONS.join(', ')}.`,
        );
        return;
      }

      if (file.size > MAX_ARTIFACT_CODE_BYTES) {
        setFileError(
          `Le fichier « ${file.name} » pèse ${formatBytes(file.size)}, ` +
            `au-delà de la limite de ${formatBytes(MAX_ARTIFACT_CODE_BYTES)}. ` +
            'Retirez les données embarquées ou découpez votre application.',
        );
        return;
      }

      const reader = new FileReader();
      reader.onerror = () => setFileError(`Le fichier « ${file.name} » n'a pas pu être lu.`);
      reader.onload = () => onChange(String(reader.result ?? ''));
      reader.readAsText(file);
    },
    [onChange],
  );

  const onDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files.item(0);
    if (file) loadFile(file);
  };

  /**
   * La doublure colorisée suit le défilement **horizontal** de la zone de saisie.
   *
   * Seul cet axe subsiste : la hauteur suit le contenu, donc rien ne défile
   * verticalement à l'intérieur du champ. Sans cette synchronisation, la
   * coloration resterait figée dès qu'on parcourt une ligne longue.
   */
  const onScroll = (event: UIEvent<HTMLTextAreaElement>): void => {
    if (highlightRef.current) {
      highlightRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  };

  return (
    <div>
      <Label
        htmlFor={textareaId}
        required
        hint={
          <>
            {formatNumber(value.length)} caractères · {formatNumber(lines.length)} lignes ·{' '}
            <span className={clsx(tooLarge && 'font-semibold text-red-600 dark:text-red-400')}>
              {formatBytes(size)} / {formatBytes(MAX_ARTIFACT_CODE_BYTES)}
            </span>
          </>
        }
      >
        {label}
      </Label>

      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={clsx(
          'relative overflow-hidden rounded-md border transition-colors',
          dragging
            ? 'border-indigo-500 ring-2 ring-indigo-500/40'
            : 'border-slate-300 dark:border-slate-700',
        )}
      >
        {/*
          Une seule hauteur pour trois couches, et elle vient du contenu.

          La zone de saisie est seule dans le flux et sa hauteur est calculée sur le
          nombre de lignes : rien ne défile verticalement à l'intérieur du champ,
          c'est la page qui défile. La numérotation et la doublure colorisée sont
          superposées en absolu et suivent donc cette hauteur.

          Deux tentatives ont précédé celle-ci, et leurs défauts expliquent sa forme.
          Une rangée flexible laissait la numérotation — une ligne par ligne de
          code — étirer la rangée à la hauteur du fichier entier, et la doublure
          dépassait de la zone de saisie : le code se poursuivait sous la barre de
          défilement horizontale, sans être modifiable. Une hauteur fixe en `rows`
          rendait ensuite ce dépassement impossible, mais au prix d'une barre
          verticale et d'une poignée de redimensionnement dans un champ dont on veut
          surtout qu'il se lise.
        */}
        <div className="relative bg-slate-50 dark:bg-slate-950">
          {/* Numérotation : purement décorative, masquée aux lecteurs d'écran. */}
          <div
            ref={gutterRef}
            aria-hidden="true"
            style={{ width: GUTTER_WIDTH }}
            className="pointer-events-none absolute bottom-0 left-0 top-0 select-none overflow-hidden border-r border-slate-200 bg-slate-100 px-2 py-3 text-right font-mono text-xs leading-5 text-slate-400 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-600"
          >
            {lines.map((_unused, index) => (
              <div key={index}>{index + 1}</div>
            ))}
          </div>

          {/* Doublure colorisée. Purement visuelle : le texte lisible par les
              outils d'assistance est celui de la zone de saisie. */}
          {highlighted ? (
            <pre
              ref={highlightRef}
              aria-hidden="true"
              style={{ paddingLeft: CODE_LEFT_PADDING }}
              className={clsx(
                'pointer-events-none absolute inset-0 overflow-hidden',
                CODE_TYPOGRAPHY,
                'text-slate-800 dark:text-slate-200',
              )}
            >
              {/* Saut de ligne final : sans lui, la dernière ligne vide du code
                  n'occupe aucune hauteur et la doublure se décale d'un cran. */}
              <code dangerouslySetInnerHTML={{ __html: `${highlighted.html}\n` }} />
            </pre>
          ) : null}

          <textarea
            id={textareaId}
            value={value}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            // Pas de retour à la ligne automatique : c'est le comportement d'un
            // éditeur de code, et la numérotation redevient exacte.
            wrap="off"
            onScroll={onScroll}
            onChange={(event) => onChange(event.target.value)}
            placeholder="Collez ici le code de votre artifact, ou déposez un fichier…"
            style={{
              paddingLeft: CODE_LEFT_PADDING,
              height: `${heightRem}rem`,
              // Le défilement horizontal reste nécessaire — une ligne longue ne se
              // replie pas — mais il est discret et cantonné au bas du champ.
              overflowY: 'hidden',
            }}
            className={clsx(
              'code-scroll relative block w-full resize-none bg-transparent',
              CODE_TYPOGRAPHY,
              // Le texte est rendu transparent **uniquement** quand la doublure
              // l'affiche à sa place. Sinon le champ paraîtrait vide.
              highlighted
                ? 'text-transparent caret-slate-900 selection:bg-indigo-500/30 dark:caret-slate-100'
                : 'text-slate-800 dark:text-slate-200',
              'placeholder:text-slate-400',
              'outline-none focus:ring-0',
            )}
          />
        </div>

        <div
          ref={footerRef}
          className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-white px-3 py-2 text-xs dark:border-slate-800 dark:bg-slate-900"
        >
          <span className="text-slate-500 dark:text-slate-400">
            {/* Le motif du repli est dit : sans coloration, rien ne distingue un
                fichier trop volumineux d'une panne de chargement. */}
            {unavailable === 'trop-volumineux'
              ? `Coloration désactivée au-delà de ${formatNumber(MAX_HIGHLIGHT_CHARACTERS)} caractères`
              : unavailable === 'indisponible'
                ? 'Coloration indisponible : le code reste modifiable'
                : `Glissez-déposez un fichier ${ACCEPTED_ARTIFACT_EXTENSIONS.join(' ')} dans la zone`}
          </span>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={clsx(
              'inline-flex items-center gap-1.5 rounded px-2 py-1 font-medium',
              'text-indigo-700 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-950',
              FOCUS_RING,
            )}
          >
            <Upload className="h-3.5 w-3.5" aria-hidden="true" />
            Choisir un fichier
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.item(0);
              if (file) loadFile(file);
              // Réinitialisé pour que le même fichier puisse être redéposé.
              event.target.value = '';
            }}
          />
        </div>
      </div>

      {fileError ? (
        <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
          {fileError}
        </p>
      ) : null}

      {tooLarge ? (
        <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
          Le code collé pèse {formatBytes(size)} et dépasse la limite de{' '}
          {formatBytes(MAX_ARTIFACT_CODE_BYTES)}.
        </p>
      ) : null}
    </div>
  );
}
